import { createAudioContext } from './audio-context';

/**
 * Short tones marking the start and end of voice capture.
 *
 * Recording starts and stops on a single button whose visual change is easy to
 * miss, especially when your eyes are on what you are about to dictate rather
 * than on the composer. A cue confirms the transition without needing to look.
 *
 * Synthesised rather than shipped as audio files: two sine blips cost nothing
 * to download, and there is no asset to go missing at runtime.
 */

type CueShape = {
  /** Hz at the start and end of the sweep. */
  from: number;
  to: number;
  durationMs: number;
};

/** Rising: capture has begun. */
const START_CUE: CueShape = { from: 620, to: 880, durationMs: 110 };

/** Falling, and a touch longer, so the two are told apart without looking. */
const STOP_CUE: CueShape = { from: 760, to: 480, durationMs: 140 };

/** Quiet enough to sit under speech, loud enough to register. */
const PEAK_GAIN = 0.05;

let context: AudioContext | null = null;

/** One context reused across cues; constructing one per blip leaks handles. */
const audioContext = (): AudioContext | null => {
  context = context ?? createAudioContext();
  return context;
};

const playCue = ({ from, to, durationMs }: CueShape): void => {
  const ctx = audioContext();
  if (ctx === null) {
    return;
  }
  try {
    // The context starts suspended until a gesture unlocks it. Both cues follow
    // a click, so resuming here is always allowed.
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }

    const now = ctx.currentTime;
    const seconds = durationMs / 1000;

    const oscillator = ctx.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(to, now + seconds);

    // Ramped rather than switched: an instant start or stop on a sine wave
    // clicks audibly.
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(PEAK_GAIN, now + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + seconds);

    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start(now);
    oscillator.stop(now + seconds + 0.02);
  } catch {
    // As above: the cue is a courtesy, never a blocker.
  }
};

export const playRecordingStartCue = (): void => playCue(START_CUE);
export const playRecordingStopCue = (): void => playCue(STOP_CUE);
